import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Milestone, HeartHandshake, Award, Briefcase, Image,
  Newspaper, AlertCircle, HelpCircle, Settings, Building2, Search, LogOut,
  Inbox, Menu, X, CreditCard, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/ward-issues", label: "Ward Issues", icon: AlertCircle },
  { to: "/admin/submissions", label: "Inquiries & Joins", icon: Inbox },
  { to: "/admin/resource/social-work", label: "Social Work", icon: HeartHandshake },
  { to: "/admin/resource/milestones", label: "Milestones", icon: Milestone },
  { to: "/admin/resource/awards", label: "Awards", icon: Award },
  { to: "/admin/resource/leadership-roles", label: "Leadership Roles", icon: Briefcase },
  { to: "/admin/resource/gallery", label: "Gallery", icon: Image },
  { to: "/admin/resource/media", label: "Media / Press", icon: Newspaper },
  { to: "/admin/resource/facts", label: "Facts (FAQ)", icon: HelpCircle },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/digital-card", label: "Digital Card", icon: CreditCard },
  { to: "/admin/biography", label: "Biography", icon: BookOpen },
  { to: "/admin/trust", label: "Charitable Trust", icon: Building2 },
  { to: "/admin/resource/page-seo", label: "SEO Settings", icon: Search },
];

export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const logout = () => { signOut(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gold flex items-center justify-center font-display font-bold text-navy">G</div>
          <div>
            <p className="text-white font-display font-semibold text-sm">Admin Panel</p>
            <p className="text-[10px] text-gold uppercase tracking-wide">Girish Naidu</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-gold text-navy" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <l.icon className="h-4 w-4 shrink-0" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <p className="text-xs text-slate-400 px-3 mb-2 truncate">{user?.email}</p>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/10 transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
            <Menu className="h-5 w-5 text-navy" />
          </button>
          <p className="font-display font-semibold text-navy">Admin</p>
          <div className="w-9" />
        </header>
        <main className="p-5 md:p-8 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
